import { useEffect, useMemo, useRef, useState, memo } from 'react';

export type PageRowThumbnailProps = {
  pageId: string;
  title: string;
  html?: string | null;
};

// Strip executable content from the HTML before mounting it inside the
// preview iframe. We render with `sandbox=""` (no allow-scripts) so the
// browser already blocks <script> execution, but we strip them belt-and-
// suspenders to avoid surprises with inline event handlers and to keep
// the srcDoc tighter. `autoplay` on media elements is also stripped so
// rows don't start playing audio/video as the user scrolls past.
const SCRIPT_STRIP = /<script\b[^>]*>[\s\S]*?<\/script>|<script\b[^>]*\/?>/gi;
const AUTOPLAY_STRIP = /(<(?:video|audio|iframe)\b[^>]*?)\sautoplay(=(?:"[^"]*"|'[^']*'|[^\s>]+))?/gi;

// CSP meta tag injected into the srcdoc to forbid scripts at the document
// level. This silences Chrome's "Blocked script execution in 'about:srcdoc'"
// console warnings — without it, ANY <script> tag the strip regex misses
// (or any inline `on*` handler) would log one warning per occurrence, which
// floods devtools when a long list of thumbnails is rendered. Behaviour is
// unchanged because the empty sandbox already blocked execution; this just
// silences the chatter.
const CSP_META = '<meta http-equiv="Content-Security-Policy" content="script-src \'none\'">';

export function prepareIframeHtml(html: string): string {
  const stripped = html.replace(SCRIPT_STRIP, '').replace(AUTOPLAY_STRIP, '$1');
  // Inject CSP meta into <head> if present, otherwise prepend to the doc so
  // it still applies. <head> match is case-insensitive and whitespace-tolerant.
  const headMatch = /<head\b[^>]*>/i.exec(stripped);
  if (headMatch) {
    const insertAt = headMatch.index + headMatch[0].length;
    return stripped.slice(0, insertAt) + CSP_META + stripped.slice(insertAt);
  }
  return CSP_META + stripped;
}

function PageRowThumbnailImpl({ pageId, title, html }: PageRowThumbnailProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  // Memoize the prepared HTML so the iframe srcDoc identity is stable
  // across mount/unmount cycles driven by IntersectionObserver. Without
  // this, scrolling out and back into view recomputes the regex strip +
  // CSP injection, allocates a new string, and forces Chrome to reparse
  // the iframe document on every scroll-in.
  const preparedHtml = useMemo(() => (html ? prepareIframeHtml(html) : ''), [html]);

  // IntersectionObserver controls whether we mount/unmount the iframe so
  // a long list with thousands of rows only pays for what's near the
  // viewport. `rootMargin` of 600px is generous so scroll-into-view feels
  // instant; `threshold: 0` flips us back out of view as soon as the row
  // leaves the margin so we shed iframe cost when scrolling away.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Non-browser / older environment — render eagerly so the rest of
      // the pipeline still exercises in tests.
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) setInView(entry.isIntersecting);
      },
      { rootMargin: '600px 0px', threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  if (!html) {
    return (
      <div
        className="pages-item-thumb pages-item-thumb--no-preview"
        ref={ref}
        data-page-id={pageId}
        aria-hidden="true"
      >
        No preview
      </div>
    );
  }

  // The iframe is laid out at the larger desktop viewport size (1280×960)
  // and scaled down via CSS transform to fit the 240×180 container — the
  // same trick CodePen, JSFiddle, Webflow, and Framer use for list-view
  // previews. The 1280→240 (5.3×) shrink keeps hero content legible in
  // the row while still reading like a desktop screenshot. See
  // PagesModule.css `.pages-item-thumb__iframe` for the exact transform.
  return (
    <div
      ref={ref}
      className="pages-item-thumb"
      data-page-id={pageId}
      aria-hidden="true"
    >
      {inView && (
        <iframe
          className="pages-item-thumb__iframe"
          sandbox=""
          srcDoc={preparedHtml}
          loading="lazy"
          tabIndex={-1}
          aria-hidden="true"
          title={`Preview ${title}`}
        />
      )}
    </div>
  );
}

export default memo(PageRowThumbnailImpl);
